/* eslint-disable better-mutation/no-mutation */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getInitialState } from './get-initial-state';
import type { InitData } from 'tradera/state/init-data/types';
import type { MemberSessionDataApiResponse } from '../services/types/webapi-member-generated';

export type MemberState = {
    // from init-data
    memberCountryCodeIso2: string;
    isCompany: boolean;
    cleanedMemberAlias: string | null;
    facebookId: string | null;
    isLoggedIn: boolean;
    hasLoggedIn: boolean; // TGL-369 Add cookie tracking if a member has logged in at least once
    previousLoginMethod: string | null;
    memberAlias: string | null;
    memberId: number;
    memberDetailedSellerRatingAverage: number; // float
    memberCountry: string | null;
    memberEmail: string | null;
    memberEmailMd5: string | null;
    memberEmailSha256: string | null;
    memberFirstName: string | null;
    memberLastName: string | null;
    memberBirthdate: string | null;
    memberTown: string | null;
    memberHeroImage: {
        max: string | null;
        med: string | null;
        min: string | null;
    };
    hasTraderaPay: boolean;
    age: number | null;
    gender: string | null;
    registrationDate: string | null;
    memberIsBuyer: boolean;
    memberIsSeller: boolean;
    // from reducer state
    isFetchingGeolocation: boolean;
    geolocation: {
        isoCode: string;
    } | null; //KATS: Do we have this type for this that we can use?
    currencyCode: string | null;
    permissions: MemberSessionDataApiResponse['permissions'];
    isImpersonationSession: boolean;
    blueshiftEventSignature: string | null;
    memberIdHash: string | null;
    automaticTranslationPreferred: boolean;
    memberState: number;
    isTrustedMember: boolean;
};

const initialState: MemberState = {
    isLoggedIn: false,
    hasLoggedIn: false,
    previousLoginMethod: null,
    isFetchingGeolocation: false,
    isImpersonationSession: false,
    geolocation: null,
    memberHeroImage: {
        max: null,
        med: null,
        min: null
    },
    memberCountryCodeIso2: '',
    isCompany: false,
    cleanedMemberAlias: null,
    facebookId: null,
    memberAlias: null,
    memberDetailedSellerRatingAverage: 0,
    memberCountry: null,
    memberEmail: null,
    memberEmailMd5: null,
    memberEmailSha256: null,
    memberFirstName: null,
    memberLastName: null,
    memberBirthdate: null,
    memberTown: null,
    hasTraderaPay: false,
    age: null,
    gender: null,
    registrationDate: null,
    memberIsBuyer: false,
    memberIsSeller: false,
    memberId: 0,
    currencyCode: null,
    permissions: null,
    blueshiftEventSignature: null,
    memberIdHash: null,
    automaticTranslationPreferred: false,
    memberState: -1,
    isTrustedMember: false
};

const memberSlice = createSlice({
    name: 'member',
    initialState,
    reducers: {
        initializeFromInitData: (state, action: PayloadAction<InitData>) => {
            return { ...state, ...getInitialState(action.payload) };
        },
        initialize: (
            state,
            action: PayloadAction<MemberSessionDataApiResponse>
        ) => {
            return { ...state, ...action.payload };
        },
        setIsFetchingGeolocation: (state, action) => {
            state.isFetchingGeolocation = action.payload.isFetchingGeolocation;
        },
        setIsImpersonationSession: (state, action: PayloadAction<boolean>) => {
            state.isImpersonationSession = action.payload;
        },
        setGeolocation: (state, action) => {
            state.geolocation = action.payload;
        },
        failedLoadingGeolocation: (state) => {
            state.geolocation = null;
            state.isFetchingGeolocation = false;
        },
        setMemberHeroImage: (state, action: PayloadAction<string>) => {
            state.memberHeroImage.max = action.payload;
            state.memberHeroImage.med = action.payload;
            state.memberHeroImage.min = action.payload;
        },
        setHasLoggedIn: (state, action: PayloadAction<boolean>) => {
            state.hasLoggedIn = action.payload;
        },
        setPreviousLoginMethod: (state, action: PayloadAction<string>) => {
            state.previousLoginMethod = action.payload;
        },
        setMemberActive: (state) => {
            state.memberState = 2000;
        }
    }
});

export const {
    initialize,
    setIsFetchingGeolocation,
    setIsImpersonationSession,
    setGeolocation,
    failedLoadingGeolocation,
    setMemberHeroImage,
    initializeFromInitData,
    setHasLoggedIn,
    setPreviousLoginMethod,
    setMemberActive
} = memberSlice.actions;

export default memberSlice.reducer;
